import { useCallback, useEffect, useRef, useState } from 'react';
import {
  apercuEnvoiDocument,
  creerSession,
  enregistrerParticipants,
  envoyerDocumentSession,
  genererDocument,
  getPlanification,
  supprimerSession,
  telechargerPdfDocument,
  updateSession,
} from '../services/sessionService';
import ApercuEmail from './ApercuEmail';
import { formatDate, formatDateTime } from './devisUtils';
import SelectRecherche from '../components/SelectRecherche';
import ChampMentions from '../components/ChampMentions';
import '../assets/styles/SessionsPanel.css';
import { participantsDepuisEmail } from './participantsDepuisEmail';
import { construireDossier, separerFormateur } from './dossierDepuisExtraction';
import { sessionsDepuisTexte } from './sessionsDepuisTexte';
import { getFormateurs } from '../services/formateurService';
import FormateurFormulaire from './FormateurFormulaire';
import { getFormations } from '../services/formationService';
import BarreProgression, { useProgression } from '../components/BarreProgression';

const CIVILITES = ['', 'Madame', 'Monsieur'];

/**
 * Un groupe se compare sans casse ni espaces superflus, comme côté serveur
 * (sessionService.cleGroupe) : « Groupe 1 » et « groupe 1 » sont le même.
 */
const cleGroupe = (valeur) => String(valeur || '').trim().toLowerCase();

const MODALITES = [
  { value: 'presentiel', label: 'Présentiel' },
  { value: 'inter_presentiel', label: 'Inter présentiel' },
  { value: 'distanciel', label: 'Distanciel' },
  { value: 'mixte', label: 'Présentiel / distanciel' },
];

const DOCUMENTS = [
  { type: 'convocation', label: 'Convocations', court: 'Convocations' },
  { type: 'feuille_presence', label: 'Feuille de présence', court: 'Feuille de présence' },
  // Seulement quand un formateur est assigné : sans fiche, personne à convoquer.
  { type: 'convocation_formateur', label: 'Convocation formateur', court: 'Convocation formateur' },
  // Produit avec la planification dès qu'un formateur est assigné, mais sans
  // retenir l'envoi des convocations : il n'entre pas dans les documents requis.
  { type: 'contrat_formateur', label: 'Contrat formateur', court: 'Contrat formateur', annexe: true },
];

/** Documents qu'une journée doit produire — même règle que le serveur (typesRequis). */
const documentsRequis = (session) =>
  DOCUMENTS.filter((d) => !d.annexe && (d.type !== 'convocation_formateur' || session.formateur_id));

/** Les documents montrés : les requis, et le contrat du formateur s'il y en a un. */
const documentsAffiches = (session) =>
  DOCUMENTS.filter((d) =>
    d.annexe ? Boolean(session.formateur_id) : d.type !== 'convocation_formateur' || session.formateur_id
  );

/** <input type="date"> veut AAAA-MM-JJ ; l'API renvoie un ISO complet. */
const versDateInput = (valeur) => (valeur ? String(valeur).slice(0, 10) : '');

/** Les horaires arrivent en HH:MM:SS ; <input type="time"> veut HH:MM. */
const versHeureInput = (valeur) => (valeur ? String(valeur).slice(0, 5) : '');

const ligneParticipantVide = () => ({
  id: null,
  civilite: '',
  prenom: '',
  nom: '',
  societe: '',
  qualite: 'Membre CSE',
  email: '',
  groupeLibelle: '',
});

function participantsVersFormulaire(participants) {
  return participants.map((p) => ({
    // L'id suit la ligne jusqu'à l'enregistrement : le serveur met à jour la
    // fiche existante au lieu de la réécrire — un élu déjà convoqué ne passe
    // pas pour un nouveau, et son lien d'émargement reste valable.
    id: p.id ?? null,
    civilite: p.civilite || '',
    prenom: p.prenom || '',
    nom: p.nom || '',
    societe: p.societe || '',
    qualite: p.qualite || 'Membre CSE',
    email: p.email || '',
    groupeLibelle: p.groupe_libelle || '',
  }));
}

function sessionVersFormulaire(session) {
  return {
    intitule: session.intitule || '',
    groupeLibelle: session.groupe_libelle || '',
    jours: (session.jours || []).map(versDateInput),
    heureDebut: versHeureInput(session.heure_debut) || '09:00',
    heureFin: versHeureInput(session.heure_fin) || '17:00',
    modalite: session.modalite || 'presentiel',
    lieu: session.lieu || '',
    adresse: session.adresse || '',
    formateurs: session.formateurs || '',
    formateurEmail: session.formateur_email || '',
    formateurId: session.formateur_id ? String(session.formateur_id) : '',
  };
}

const formulaireSessionVide = () => ({
  intitule: '',
  groupeLibelle: '',
  jours: [],
  heureDebut: '09:00',
  heureFin: '17:00',
  modalite: 'presentiel',
  lieu: '',
  adresse: '',
  formateurs: '',
  formateurEmail: '',
  formateurId: '',
});

/**
 * Éclate une journée en pastille de calendrier — « mar. / 16 / sept. » — et
 * la situe dans le temps : une journée déjà passée ne doit pas se lire comme
 * une échéance à préparer.
 */
function pastilleJour(valeur) {
  const d = new Date(valeur);
  if (Number.isNaN(d.getTime())) return null;

  const minuit = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const ecart = minuit(d) - minuit(new Date());

  return {
    semaine: d.toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', ''),
    jour: d.toLocaleDateString('fr-FR', { day: '2-digit' }),
    mois: d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', ''),
    etat: ecart === 0 ? 'aujourdhui' : ecart < 0 ? 'passee' : 'avenir',
  };
}

/**
 * Sessions planifiées, participants et documents qui en découlent
 * (convocations, feuille de présence).
 *
 * Ouvert depuis le bloc « Contrat de formation » du détail d'une demande :
 * le contrat dit quoi est vendu, ce panneau dit quand, où et avec qui.
 */
function SessionsPanel({ contrat, onClose }) {
  const [sessions, setSessions] = useState([]);
  const [calendrier, setCalendrier] = useState([]);
  const [participants, setParticipants] = useState([]);

  const [chargement, setChargement] = useState(true);
  // Un chargement en échec laisse le panneau vide : c'est l'état de la page,
  // pas une notification qui s'efface au bout de quelques secondes.
  const [erreurChargement, setErreurChargement] = useState(null);
  const [notifs, setNotifs] = useState([]);

  // Email collé par l'utilisateur, d'où l'on tire la liste des élus.
  const [emailColle, setEmailColle] = useState('');
  // La saisie manuelle des participants (grille ligne à ligne) est repliée
  // par défaut : coller l'email du client suffit le plus souvent. Elle
  // s'ouvre d'un clic, et d'elle-même quand l'email vient de la remplir.
  const [saisieOuverte, setSaisieOuverte] = useState(false);

  // Les élus qu'on ne pourra ni convoquer ni faire signer en ligne. On
  // compte sur le nom : une ligne vide laissée par le formulaire n'est pas
  // un participant sans adresse.
  const sansEmail = participants.filter(
    (p) => String(p.nom || '').trim() && !String(p.email || '').trim()
  ).length;
  // Fiches formateurs proposées au choix sur chaque journée.
  const [formateurs, setFormateurs] = useState([]);
  // Création d'une fiche formateur sans quitter la planification : d'où elle
  // a été demandée ('edition' | 'manuel' | 'ajout'), pour l'assigner aussitôt.
  const [creationFormateur, setCreationFormateur] = useState(null);
  // Le catalogue, pour reconnaître « SSCT », « économique »… dans un texte.
  const [catalogue, setCatalogue] = useState([]);
  const [catalogueCharge, setCatalogueCharge] = useState(false);
  // Ce que la demande de devis sait déjà (message, mail, pièces lues,
  // lignes du devis, adresse) : le panneau s'en remplit à l'ouverture.
  const [demande, setDemande] = useState(null);
  // Une seule fois par ouverture : reproposer à chaque rechargement
  // doublerait les brouillons.
  const preRempli = useRef(false);

  // Ajout d'une session : depuis le calendrier, ou dates saisies à la main.
  const [calendrierChoisi, setCalendrierChoisi] = useState('');
  const [groupeAjout, setGroupeAjout] = useState('');
  // Fiche formateur choisie à l'ajout depuis le calendrier ; '' = plus tard.
  const [formateurAjout, setFormateurAjout] = useState('');
  const [saisieManuelle, setSaisieManuelle] = useState(false);
  const [formManuel, setFormManuel] = useState(formulaireSessionVide);

  // Session en cours de modification, et travail en cours (documents).
  const [edition, setEdition] = useState(null);
  const [travail, setTravail] = useState(null);

  /* ---------------- Brouillon ----------------

     Rien de ce qu'on fait dans ce panneau ne part au serveur avant le bouton
     « Enregistrer la planification », en bas. Ce n'est pas un confort : chaque
     journée enregistrée produit ses documents ET déclenche les envois dus —
     une formation planifiée pour le jour même reçoit sa convocation dans la
     seconde. Une saisie en cours, une date tapée de travers, ne doivent pas
     écrire à des clients. Le bouton confirme ; avant lui, tout est révocable.

     Quatre natures de changement, rejouées dans cet ordre à la confirmation :
     participants, suppressions, modifications, ajouts. */
  const [ajouts, setAjouts] = useState([]);
  const [modifications, setModifications] = useState({});
  const [suppressions, setSuppressions] = useState(() => new Set());
  const [participantsModifies, setParticipantsModifies] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  // La liste des élus change alors qu'une convocation est déjà partie : la
  // modale demande quoi envoyer (tous, seulement les nouveaux, rien) au lieu
  // de reconvoquer tout le monde en silence.
  const [choixEnvoi, setChoixEnvoi] = useState(false);
  const progressionEnregistrement = useProgression(confirmation, { estimeeMs: 15000 });

  const enAttente =
    ajouts.length +
    Object.keys(modifications).length +
    suppressions.size +
    (participantsModifies ? 1 : 0);

  /** Toute retouche des participants passe par ici : elle les marque à enregistrer. */
  const modifierParticipants = (maj) => {
    setParticipants(maj);
    setParticipantsModifies(true);
  };

  /**
   * Le ou les formateurs de la planification, tels qu'ils figurent sur les
   * journées (celles du serveur avec leurs retouches non enregistrées, et celles
   * qu'on est en train d'ajouter). Ils s'affichent dans la liste des
   * participants, qualité « Formateur » au lieu de « Membre CSE » : on y voit
   * d'un coup d'œil qui est convoqué et qui anime. Leur choix, lui, reste sur
   * la journée — c'est elle qui porte le formateur, pas la liste des élus.
   */
  const formateursPlanif = (() => {
    const vus = new Map();
    const ajouter = (nom, email) => {
      const n = String(nom || '').trim();
      if (!n) return;
      const cle = n.toLocaleLowerCase('fr');
      const courant = vus.get(cle);
      if (!courant) vus.set(cle, { nom: n, email: String(email || '').trim() });
      else if (!courant.email && email) courant.email = String(email).trim();
    };
    for (const x of sessions) {
      if (suppressions.has(x.id)) continue;
      const f = modifications[x.id] || sessionVersFormulaire(x);
      ajouter(f.formateurs, f.formateurEmail);
    }
    for (const a of ajouts) ajouter(a.corps.formateurs, a.corps.formateurEmail);
    return [...vus.values()];
  })();

  /* Les documents d'une session tiennent en deux lignes de boutons : repliés
     par défaut, ils laissent la carte à ce qu'elle dit d'abord — quand, où,
     avec quel groupe. */
  const [documentsOuverts, setDocumentsOuverts] = useState(() => new Set());

  const basculerDocuments = (id) =>
    setDocumentsOuverts((prev) => {
      const suivant = new Set(prev);
      if (suivant.has(id)) suivant.delete(id);
      else suivant.add(id);
      return suivant;
    });

  /* ---------------- Notifications ---------------- */

  /* Empilés dans la page, les messages de retour poussaient le contenu vers
     le bas et se lisaient hors du champ de vision dès que l'action partait
     du bas du panneau : ils passent en pastilles flottantes. */
  const minuteurs = useRef([]);

  useEffect(() => () => minuteurs.current.forEach(clearTimeout), []);

  const notifier = useCallback((type, texte) => {
    if (!texte) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    setNotifs((prev) => [...prev, { id, type, texte }]);
    minuteurs.current.push(
      setTimeout(
        () => setNotifs((prev) => prev.filter((n) => n.id !== id)),
        // Une erreur se lit et se comprend : elle reste plus longtemps. Un
        // compte rendu long (documents produits) aussi.
        type === 'erreur' ? 8000 : Math.min(9000, 4500 + texte.length * 25)
      )
    );
  }, []);

  const fermerNotif = (id) => setNotifs((prev) => prev.filter((n) => n.id !== id));

  // `garderParticipants` : un enregistrement dont les participants ont
  // échoué recharge les journées, mais ne doit pas écraser la saisie qu'on
  // n'a pas pu enregistrer.
  const charger = useCallback(async ({ garderParticipants = false } = {}) => {
    setChargement(true);
    setErreurChargement(null);
    try {
      const res = await getPlanification(contrat.id);
      setSessions(res.data.sessions || []);
      setCalendrier(res.data.calendrier || []);
      setDemande(res.data.demande || null);
      if (!garderParticipants) {
        setParticipants(participantsVersFormulaire(res.data.participants || []));
      }
      // L'email des élus collé sur la demande : proposé dans la zone tant
      // qu'elle est vide et qu'aucun participant n'est encore enregistré.
      // Une fois la liste posée, le reproposer pousserait à l'ajouter deux fois.
      if (res.data.mailPlanification && !(res.data.participants || []).length) {
        setEmailColle((prev) => prev || res.data.mailPlanification);
      }
    } catch (err) {
      setErreurChargement(err.message || 'Chargement de la planification impossible.');
    } finally {
      setChargement(false);
    }
  }, [contrat.id]);

  useEffect(() => {
    charger();
  }, [charger]);

  // Les fiches formateurs, chargées une fois : elles ne changent pas
  // pendant qu'on planifie. Sans elles, la saisie libre reste possible.
  useEffect(() => {
    let vivant = true;
    getFormateurs({ actif: true })
      .then((res) => {
        if (vivant) setFormateurs(res.data || []);
      })
      .catch(() => {});
    getFormations({ actif: true })
      .then((res) => {
        if (vivant) setCatalogue(res.data || []);
      })
      .catch(() => {})
      .finally(() => {
        if (vivant) setCatalogueCharge(true);
      });
    return () => {
      vivant = false;
    };
  }, []);

  /* ---------------- Participants ---------------- */

  const majParticipant = (index, champ, valeur) =>
    modifierParticipants((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [champ]: valeur } : p))
    );

  /**
   * « @ » dans le nom d'un participant, puis une suggestion : la ligne se remplit
   * (prénom, nom, entreprise, adresse). Un formateur n'est pas un élu : il se pose
   * sur les journées, comme quand on le colle depuis un email.
   */
  function choisirPersonneDansLigne(index, p) {
    const nom = [p.prenom, String(p.nom || '').toLocaleUpperCase('fr')].filter(Boolean).join(' ');
    if (p.types?.includes('formateur')) {
      poserFormateur({ nomComplet: nom, email: p.email || '' });
      notifier('succes', `Formateur ${nom} posé sur les journées sans formateur.`);
      return;
    }
    modifierParticipants((prev) =>
      prev.map((x, i) =>
        i === index
          ? {
              ...x,
              prenom: p.prenom || x.prenom,
              nom: p.nom ? String(p.nom).toLocaleUpperCase('fr') : x.nom,
              societe: p.societe || x.societe || '',
              email: p.email || x.email,
            }
          : x
      )
    );
  }

  /* ---------------- Journées lues dans un texte ---------------- */

  /** Le lieu d'une session INTRA quand le texte ne le dit pas : celui du contrat, sinon l'adresse du client. */
  const lieuParDefaut = () =>
    contrat.lieu_formation ||
    demande?.modalites_lieu ||
    [demande?.adresse, [demande?.code_postal, demande?.ville].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(', ') ||
    '';

  /** Vrai si une journée existante ou déjà en brouillon couvre cette proposition. */
  const dejaPlanifiee = (p, brouillons = ajouts) => {
    const jours = new Set(p.jours || []);
    const memesJours = (liste) => {
      const l = (liste || []).map(versDateInput).filter(Boolean);
      return l.length > 0 && [...jours].every((j) => l.includes(j));
    };
    if (
      sessions.some(
        (x) =>
          !suppressions.has(x.id) &&
          ((p.calendrierId && String(x.calendrier_id) === String(p.calendrierId)) ||
            (jours.size && memesJours(x.jours)))
      )
    ) {
      return true;
    }
    return brouillons.some(
      (a) =>
        (p.calendrierId && String(a.corps.calendrierId) === String(p.calendrierId)) ||
        (jours.size && memesJours(a.corps.jours))
    );
  };

  /** Un brouillon d'ajout depuis une proposition lue (texte, pièces, devis). */
  const versAjout = (p, source) => {
    const c = p.calendrierId
      ? calendrier.find((x) => String(x.calendrier_id) === String(p.calendrierId))
      : null;
    if (p.calendrierId) {
      return {
        cle: `lu-cal-${p.calendrierId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        corps: {
          calendrierId: Number(p.calendrierId),
          groupeLibelle: p.groupeLibelle || null,
          formateurId: p.formateurId || null,
          formateurs: p.formateurs || null,
          formateurEmail: p.formateurEmail || null,
        },
        apercu: {
          intitule: c
            ? `${c.intitule_calendrier}${c.module ? ` (${c.module})` : ''}`
            : p.intitule || 'Session du calendrier',
          periode: c
            ? `${formatDate(c.date_debut)} → ${formatDate(c.date_fin)}`
            : (p.jours || []).map((j) => formatDate(j)).join(', '),
          groupe: p.groupeLibelle || '',
          source,
        },
      };
    }
    const jours = [...(p.jours || [])].filter(Boolean).sort();
    return {
      cle: `lu-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      corps: {
        ...formulaireSessionVide(),
        intitule: p.intitule || contrat.intitule_action || '',
        groupeLibelle: p.groupeLibelle || '',
        jours,
        modalite: p.modalite || 'presentiel',
        lieu: p.lieu || lieuParDefaut(),
        formateurId: p.formateurId || '',
        formateurs: p.formateurs || '',
        formateurEmail: p.formateurEmail || '',
      },
      apercu: {
        intitule: p.intitule || contrat.intitule_action || 'Session',
        periode: jours.map((j) => formatDate(j)).join(', '),
        groupe: p.groupeLibelle || '',
        source,
      },
    };
  };

  /**
   * Lit les dates de formation d'un texte (« SSCT du 23 septembre ») et
   * les met en brouillon. Renvoie le nombre de journées proposées.
   */
  function proposerJournees(texte, source) {
    const trouvees = sessionsDepuisTexte(texte, {
      lignesDevis: demande?.lignes || [],
      catalogue,
      calendrier,
    });
    const nouvelles = [];
    for (const p of trouvees) {
      if (dejaPlanifiee(p, [...ajouts, ...nouvelles])) continue;
      nouvelles.push(
        versAjout(
          {
            ...p,
            modalite: String(p.formation?.format || '').includes('mixte') ? 'mixte' : String(p.formation?.format || '').includes('distanciel') ? 'distanciel' : p.formation?.format === 'inter_presentiel' ? 'inter_presentiel' : 'presentiel',
          },
          source
        )
      );
    }
    if (nouvelles.length) setAjouts((prev) => [...prev, ...nouvelles]);
    return nouvelles.length;
  }

  /** Pose un formateur lu sur les journées qui n'en ont pas encore. */
  function poserFormateur(formateur) {
    if (!formateur) return;
    const poser = (f) =>
      f.formateurId || String(f.formateurs || '').trim()
        ? { ...f, formateurEmail: f.formateurEmail || formateur.email }
        : { ...f, formateurs: formateur.nomComplet, formateurEmail: formateur.email };
    setModifications((prev) => {
      const suite = { ...prev };
      for (const x of sessions) {
        if (suppressions.has(x.id)) continue;
        const base = suite[x.id] || sessionVersFormulaire(x);
        const apres = poser(base);
        if (apres.formateurs !== base.formateurs || apres.formateurEmail !== base.formateurEmail) {
          suite[x.id] = apres;
        }
      }
      return suite;
    });
    setAjouts((prev) => prev.map((a) => ({ ...a, corps: poser(a.corps) })));
    setFormManuel((f) => poser(f));
  }

  /**
   * Lit l'email collé : les participants (une adresse par ligne), le
   * formateur (« Formateur : … »), et les dates de formation choisies
   * (« SSCT du 23 septembre »), mises en brouillon de journée.
   *
   * Ajout et non remplacement : coller est un geste qu'on peut répéter, et
   * écraser une saisie déjà faite serait une perte silencieuse. Les adresses
   * déjà présentes sont ignorées ; les lignes restées vides sont nettoyées
   * au passage.
   */
  function remplirDepuisEmail() {
    const lus = participantsDepuisEmail(emailColle);
    const nbJournees = proposerJournees(emailColle, 'Email du client');
    if (!lus.length && !nbJournees) {
      notifier(
        'erreur',
        'Aucune adresse email ni date trouvée dans ce message.'
      );
      return;
    }

    // La ligne « Formateur : … » du mail n'est pas un élu : elle va sur les
    // journées qui n'ont pas encore de formateur — celles du serveur (en
    // brouillon de modification), celles ajoutées mais pas encore créées, et
    // le formulaire de saisie manuelle s'il est ouvert. Une journée qui a
    // déjà son formateur ne reçoit que l'adresse si elle lui manque.
    const { participants: trouves, formateur } = separerFormateur(lus);
    poserFormateur(formateur);

    const connus = new Set(
      participants.map((p) => p.email.trim().toLowerCase()).filter(Boolean)
    );
    const nouveaux = trouves.filter((p) => !connus.has(p.email));

    if (nouveaux.length) {
      setSaisieOuverte(true);
      modifierParticipants((prev) => [
        ...prev.filter((p) => p.nom.trim() || p.email.trim()),
        ...nouveaux,
      ]);
    }
    setEmailColle('');

    const ignores = trouves.length - nouveaux.length;
    const bouts = [];
    if (trouves.length) {
      bouts.push(
        `${nouveaux.length} participant(s) ajouté(s)` + (ignores ? `, ${ignores} déjà présent(s)` : '')
      );
    }
    if (formateur) {
      bouts.push(
        `formateur reconnu : ${formateur.nomComplet}${
          formateur.email ? ` (${formateur.email})` : ''
        }, posé sur les journées sans formateur`
      );
    }
    if (nbJournees) {
      bouts.push(`${nbJournees} journée(s) proposée(s) en brouillon`);
    }
    notifier(
      'succes',
      `${bouts.join(' ; ')}. Vérifiez, puis « Enregistrer la planification ».`
    );
  }

  /**
   * À l'ouverture d'une planification encore vide : tout ce que la demande
   * de devis sait déjà est proposé d'office — les sessions du calendrier
   * retenues au devis, les journées et participants lus dans les pièces,
   * les élus et les dates écrits dans le mail du client ou le message de la
   * demande. En brouillon : rien ne part avant « Enregistrer ».
   */
  useEffect(() => {
    if (chargement || preRempli.current || !demande || !catalogueCharge) return;
    preRempli.current = true;
    if (sessions.length) return;

    const texte = [demande.mail_planification, demande.message].filter(Boolean).join('\n');
    const brouillons = [];
    const compte = { calendrier: 0, pieces: 0, texte: 0, participants: 0 };

    // 1. Les sessions du calendrier que le devis a retenues.
    for (const l of demande.lignes || []) {
      if (!l.calendrier_id) continue;
      const p = { calendrierId: String(l.calendrier_id), groupeLibelle: l.groupe_libelle || '' };
      if (dejaPlanifiee(p, brouillons)) continue;
      brouillons.push(versAjout(p, 'Devis — session retenue'));
      compte.calendrier += 1;
    }

    // 2. Les pièces lues et le mail : formations datées, participants, formateur.
    const dossier = construireDossier({
      extraction: demande.extraction_pieces,
      mailPlanification: texte,
      catalogue,
      formateurs,
      calendrier: [],
    });
    for (const j of dossier.journees || []) {
      const p = {
        intitule: j.intitule,
        groupeLibelle: j.groupeLibelle,
        jours: j.jours,
        modalite: j.modalite,
        lieu: j.lieu,
        formateurId: j.formateurId,
        formateurs: j.formateurs,
        formateurEmail: j.formateurEmail,
      };
      if (!p.jours?.length || dejaPlanifiee(p, brouillons)) continue;
      brouillons.push(versAjout(p, 'Pièces de la demande'));
      compte.pieces += 1;
    }

    // 3. Les dates écrites en clair dans le mail ou le message.
    for (const p of sessionsDepuisTexte(texte, {
      lignesDevis: demande.lignes || [],
      catalogue,
      calendrier,
    })) {
      if (dejaPlanifiee(p, brouillons)) continue;
      brouillons.push(
        versAjout(
          {
            ...p,
            modalite: String(p.formation?.format || '').includes('mixte') ? 'mixte' : String(p.formation?.format || '').includes('distanciel') ? 'distanciel' : p.formation?.format === 'inter_presentiel' ? 'inter_presentiel' : 'presentiel',
            formateurId: dossier.formateur?.formateurId || '',
            formateurs: dossier.formateur?.formateurs || '',
            formateurEmail: dossier.formateur?.formateurEmail || '',
          },
          'Demande de devis'
        )
      );
      compte.texte += 1;
    }

    if (brouillons.length) setAjouts((prev) => [...prev, ...brouillons]);

    // 4. Les participants, s'il n'y en a pas encore.
    if (!participants.length && dossier.participants.length) {
      modifierParticipants(dossier.participants);
      setSaisieOuverte(true);
      // Le mail vient d'être lu : le reproposer dans la zone pousserait à
      // l'ajouter deux fois.
      setEmailColle('');
      compte.participants = dossier.participants.length;
    }

    const total = compte.calendrier + compte.pieces + compte.texte;
    if (!total && !compte.participants) return;
    const bouts = [];
    if (total) {
      const origines = [
        compte.calendrier ? `${compte.calendrier} retenue(s) au devis` : null,
        compte.pieces ? `${compte.pieces} lue(s) dans les pièces` : null,
        compte.texte ? `${compte.texte} lue(s) dans le message du client` : null,
      ].filter(Boolean);
      bouts.push(`${total} journée(s) proposée(s) (${origines.join(', ')})`);
    }
    if (compte.participants) bouts.push(`${compte.participants} participant(s) repris de la demande`);
    if (dossier.formateur) {
      bouts.push(`formateur : ${dossier.formateur.formateurs || 'fiche reconnue'}`);
    }
    notifier(
      'succes',
      `Pré-rempli depuis la demande : ${bouts.join(' ; ')}. Vérifiez puis enregistrez.`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargement, demande, catalogueCharge, sessions, formateurs]);

  /* ---------------- Sessions ---------------- */

  /**
   * Ajout depuis le calendrier : mis en brouillon, pas encore créé.
   *
   * On garde de quoi l'afficher — intitulé et période de la session choisie —
   * puisqu'il n'existe encore ni journée ni document côté serveur.
   */
  function ajouterDepuisCalendrier() {
    if (!calendrierChoisi) return;
    const c = calendrier.find((x) => String(x.calendrier_id) === String(calendrierChoisi));
    setAjouts((prev) => [
      ...prev,
      {
        cle: `cal-${calendrierChoisi}-${Date.now()}`,
        corps: {
          calendrierId: Number(calendrierChoisi),
          groupeLibelle: groupeAjout || null,
          formateurId: formateurAjout || null,
        },
        apercu: {
          intitule: c
            ? `${c.intitule_calendrier}${c.module ? ` (${c.module})` : ''}`
            : 'Session du calendrier',
          periode: c ? `${formatDate(c.date_debut)} → ${formatDate(c.date_fin)}` : '',
          groupe: groupeAjout || '',
          source: 'Calendrier',
        },
      },
    ]);
    setCalendrierChoisi('');
    setGroupeAjout('');
    setFormateurAjout('');
  }

  function ajouterManuellement() {
    if (!formManuel.jours.length) {
      notifier('erreur', 'Ajoutez au moins une journée à la session.');
      return;
    }
    const corps = { ...formManuel, intitule: formManuel.intitule || contrat.intitule_action };
    const jours = [...formManuel.jours].filter(Boolean).sort();
    setAjouts((prev) => [
      ...prev,
      {
        cle: `man-${Date.now()}`,
        corps,
        apercu: {
          intitule: corps.intitule || 'Session',
          periode: jours.map((j) => formatDate(j)).join(', '),
          groupe: formManuel.groupeLibelle || '',
          source: 'Saisie manuelle',
        },
      },
    ]);
    setFormManuel(formulaireSessionVide());
    setSaisieManuelle(false);
  }

  /** La fiche créée depuis le panneau rejoint la liste et s'assigne où on l'attendait. */
  function formateurCree(fiche) {
    setFormateurs((prev) =>
      [...prev.filter((x) => x.id !== fiche.id), fiche].sort((a, b) =>
        String(a.nom || '').localeCompare(String(b.nom || ''), 'fr')
      )
    );
    const id = String(fiche.id);
    if (creationFormateur === 'edition') {
      setEdition((prev) => (prev ? { ...prev, form: { ...prev.form, formateurId: id } } : prev));
    } else if (creationFormateur === 'manuel') {
      setFormManuel((prev) => ({ ...prev, formateurId: id }));
    } else if (creationFormateur === 'ajout') {
      setFormateurAjout(id);
    }
    setCreationFormateur(null);
  }

  /** Une journée modifiée garde sa retouche en attente, jusqu'à la confirmation. */
  function enregistrerEdition() {
    setModifications((prev) => ({ ...prev, [edition.id]: edition.form }));
    setEdition(null);
  }

  function retirerSession(session) {
    setSuppressions((prev) => new Set(prev).add(session.id));
    // Modifier puis supprimer : seule la suppression compte.
    setModifications((prev) => {
      const suite = { ...prev };
      delete suite[session.id];
      return suite;
    });
    if (edition?.id === session.id) setEdition(null);
  }

  function annulerSuppression(id) {
    setSuppressions((prev) => {
      const suite = new Set(prev);
      suite.delete(id);
      return suite;
    });
  }

  function annulerModification(id) {
    setModifications((prev) => {
      const suite = { ...prev };
      delete suite[id];
      return suite;
    });
  }

  /** Tout le brouillon est abandonné : on revient à ce que porte le serveur. */
  async function annulerTout() {
    setAjouts([]);
    setModifications({});
    setSuppressions(new Set());
    setParticipantsModifies(false);
    setEdition(null);
    await charger();
  }

  /**
   * Confirme la planification : le seul geste qui écrit, et donc le seul qui
   * produit les documents et déclenche les envois.
   *
   * Les participants d'abord, et seuls : les documents des journées portent
   * leur liste. S'ils échouent, on s'arrête là — créer des journées avec
   * l'ancienne liste produirait des convocations sans les nouveaux venus, et
   * les enverrait peut-être. Rien d'autre n'est alors écrit.
   *
   * Ensuite suppressions, modifications, ajouts. Un échec sur l'un n'arrête
   * pas les autres : chaque réussite quitte le brouillon, chaque échec y reste,
   * pour être corrigé et reconfirmé.
   */
  async function enregistrerTout(modeEnvoi = null) {
    // Des élus changent alors qu'une convocation est déjà partie : sans choix
    // explicite, on s'arrête et on demande quoi envoyer. Les journées dont la
    // convocation n'est pas partie ne sont pas concernées — leurs envois
    // suivent le circuit normal.
    const convocationPartie = sessions.some(
      (s) =>
        !suppressions.has(s.id) &&
        (s.documents || []).some((d) => d.type === 'convocation' && d.date_envoi)
    );
    if (!modeEnvoi && participantsModifies && convocationPartie) {
      setChoixEnvoi(true);
      return;
    }
    setChoixEnvoi(false);

    setConfirmation(true);
    const comptes = [];
    const echecs = [];

    try {
      if (participantsModifies) {
        try {
          const res = await enregistrerParticipants(
            contrat.id,
            participants.filter((p) => p.nom.trim()),
            modeEnvoi || 'nouveaux'
          );
          setParticipantsModifies(false);
          if (res.message) comptes.push(res.message);
        } catch (err) {
          notifier(
            'erreur',
            `Participants non enregistrés : ${err.message}. Rien d'autre n'a été enregistré.`
          );
          await charger({ garderParticipants: true });
          return;
        }
      }

      const suppressionsRestantes = new Set();
      for (const id of suppressions) {
        try {
          await supprimerSession(id);
        } catch (err) {
          suppressionsRestantes.add(id);
          echecs.push(`suppression — ${err.message}`);
        }
      }

      const modificationsRestantes = {};
      for (const [id, form] of Object.entries(modifications)) {
        try {
          const res = await updateSession(Number(id), form);
          if (res.message) comptes.push(res.message);
        } catch (err) {
          modificationsRestantes[id] = form;
          echecs.push(`modification — ${err.message}`);
        }
      }

      const ajoutsRestants = [];
      for (const a of ajouts) {
        try {
          const res = await creerSession(contrat.id, a.corps);
          if (res.message) comptes.push(res.message);
        } catch (err) {
          ajoutsRestants.push(a);
          echecs.push(`${a.apercu.intitule} — ${err.message}`);
        }
      }

      setSuppressions(suppressionsRestantes);
      setModifications(modificationsRestantes);
      setAjouts(ajoutsRestants);
      await charger();

      if (echecs.length) {
        notifier(
          'erreur',
          `Planification enregistrée en partie. Restent en brouillon : ${echecs.join(' · ')}`
        );
      } else {
        notifier('succes', comptes.join(' ') || 'Planification enregistrée.');
      }
    } finally {
      setConfirmation(false);
    }
  }

  /** Fermer avec un brouillon en cours : on le dit, plutôt que de le perdre. */
  function fermer() {
    if (
      enAttente &&
      !window.confirm(
        `${enAttente} modification(s) non enregistrée(s). Fermer sans enregistrer ? ` +
          'Rien ne sera créé ni envoyé.'
      )
    ) {
      return;
    }
    onClose();
  }

  /* ---------------- Documents ---------------- */

  async function produire(session, type) {
    setTravail(`${session.id}:${type}`);
    try {
      await genererDocument(session.id, type);
      await charger();
      notifier(
        'succes',
        `${DOCUMENTS.find((d) => d.type === type).label} : document généré.`
      );
    } catch (err) {
      notifier('erreur', err.message || 'Génération impossible.');
    } finally {
      setTravail(null);
    }
  }

  // L'aperçu de l'email avant l'envoi : le document en cours de relecture.
  const [apercuConvocation, setApercuConvocation] = useState(null);

  /** Ouvre l'aperçu de l'email ; rien ne part avant le clic dans la modale. */
  function envoyerConvocation(doc) {
    setApercuConvocation(doc);
  }

  /**
   * L'envoi réel, confirmé depuis l'aperçu. Le serveur en fait le compte
   * rendu (combien de participants, copie au client, adresses manquantes) :
   * on le reprend tel quel plutôt que de le reconstituer ici.
   */
  async function envoyerConvocationApresApercu(doc, { sujet, texte }) {
    setTravail(`envoi:${doc.id}`);
    try {
      const res = await envoyerDocumentSession(doc.id, { sujet, texte });
      await charger();
      notifier('succes', res.message || 'Convocation envoyée.');
    } finally {
      setTravail(null);
    }
  }

  async function telecharger(document) {
    setTravail(`dl:${document.id}`);
    try {
      await telechargerPdfDocument(document.id);
    } catch (err) {
      notifier('erreur', err.message || 'Téléchargement impossible.');
    } finally {
      setTravail(null);
    }
  }

  /* ---------------- Rendu ---------------- */

  /* Un groupe saisi à la main sur le participant et sur la journée, comparés
     au caractère près, suffisait à vider une feuille : « Group 1 » sur l'élu
     et « Groupe 1 » sur la journée, et plus personne n'était convoqué. Le
     champ propose désormais les groupes qui existent vraiment. */
  const groupesDesJournees = [
    ...new Map(
      sessions
        .map((s) => (s.groupe_libelle || '').trim())
        .filter(Boolean)
        .map((g) => [cleGroupe(g), g])
    ).values(),
  ];

  const suggerees = calendrier.filter((c) => c.suggeree);
  const autres = calendrier.filter((c) => !c.suggeree);

  // « retenue au devis » : le client a signé pour cette date, ses places y
  // sont déjà comptées — c'est celle-là qu'on planifie en premier.
  const optionCalendrier = (c) => (
    <option key={c.calendrier_id} value={c.calendrier_id}>
      {c.retenue_au_devis ? '★ ' : ''}
      {formatDate(c.date_debut)} → {formatDate(c.date_fin)} · {c.intitule_calendrier}
      {c.module ? ` (${c.module})` : ''} · {c.lieux || '—'}
      {c.retenue_au_devis ? ' · retenue au devis' : ''}
    </option>
  );

  return (
    <div className="devis-modal-overlay sessions-overlay" onClick={fermer}>
      {/* Posée en fixe au-dessus du panneau : son clic ne doit pas remonter
          jusqu'au voile, qui referme la planification. */}
      <div className="sessions-notifs" onClick={(e) => e.stopPropagation()}>
        {notifs.map((n) => (
          <div
            className={`sessions-notif sessions-notif-${n.type}`}
            role={n.type === 'erreur' ? 'alert' : 'status'}
            key={n.id}
          >
            <span className="sessions-notif-icone" aria-hidden="true">
              {n.type === 'erreur' ? '!' : '✓'}
            </span>
            <span className="sessions-notif-texte">{n.texte}</span>
            <button
              type="button"
              className="sessions-notif-fermer"
              onClick={() => fermerNotif(n.id)}
              aria-label="Masquer la notification"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <div className="devis-modal" onClick={(e) => e.stopPropagation()}>
        <div className="devis-modal-header">
          <div>
            <div className="devis-modal-numero">Sessions &amp; documents</div>
            <div className="devis-modal-sub">
              {contrat.numero} · {contrat.societe_nom}
            </div>
          </div>
          <div className="devis-modal-header-right">
            <button
              type="button"
              className="devis-drawer-close"
              onClick={fermer}
              aria-label="Fermer"
            >
              ×
            </button>
          </div>
        </div>

        <div className="devis-modal-body">
          {chargement && <div className="devis-drawer-empty">Chargement...</div>}
          {erreurChargement && (
            <div className="devis-alert devis-alert-erreur">{erreurChargement}</div>
          )}

          {!chargement && !erreurChargement && (
            <>
              {/* ---------------- Participants ---------------- */}
              <div className="devis-form-section-title sessions-section-titre">
                Participants
                {participants.length > 0 && (
                  <span className="sessions-compteur">{participants.length}</span>
                )}
              </div>

              {/* Le client envoie sa liste d'élus par email : plutôt que de
                  la ressaisir, on la colle et on en tire les participants. */}
              <div className="participants-import">
                <label className="participants-import-label" htmlFor="participants-email">
                  Coller l&apos;email du client (élus, formateur, dates choisies)
                </label>
                <ChampMentions
                  id="participants-email"
                  className="participants-import-zone"
                  rows={4}
                  value={emailColle}
                  onChange={(e) => setEmailColle(e.target.value)}
                  suffixe={'\n'}
                  placeholder="Message du client, ex. : DUPONT Jean - jean.dupont@exemple.fr - ACME - Secrétaire / SSCT du 23 au 25 septembre. Tapez @ pour retrouver une personne."
                />
                <div className="participants-import-actions">
                  <span className="participants-import-aide">
                    Une ligne avec email = un participant (nom - email - entreprise) ; une date = une
                    journée à planifier. Tapez @ pour retrouver un client ou un formateur.
                  </span>
                  <button
                    type="button"
                    className="devis-btn devis-btn-sm devis-btn-primary"
                    onClick={remplirDepuisEmail}
                    disabled={!emailColle.trim()}
                  >
                    Remplir depuis l&apos;email
                  </button>
                </div>
              </div>

              {/* Un élu sans adresse ne reçoit ni convocation ni lien pour
                  émarger : sa case se signera sur place. Dit ici, avant
                  l'envoi, plutôt que dans le compte rendu après coup. */}
              {sansEmail > 0 && (
                <div className="sessions-alerte sessions-alerte-participants">
                  {sansEmail} participant{sansEmail > 1 ? 's' : ''} sans adresse email :{' '}
                  {sansEmail > 1 ? 'ils ne recevront' : 'il ne recevra'} ni convocation ni lien
                  de signature. {sansEmail > 1 ? 'Leurs cases resteront' : 'Sa case restera'} à
                  émarger sur place.
                </div>
              )}

              {/* Repliée : un résumé des participants et le bouton pour ouvrir
                  la saisie manuelle. */}
              <div className="participants-repli">
                <button
                  type="button"
                  className="participants-deplier"
                  onClick={() => setSaisieOuverte((o) => !o)}
                  aria-expanded={saisieOuverte}
                >
                  <span className="participants-deplier-chevron" aria-hidden="true">
                    {saisieOuverte ? '▴' : '▾'}
                  </span>
                  {saisieOuverte ? 'Masquer la saisie manuelle' : 'Saisir ou modifier les participants à la main'}
                </button>
                {!saisieOuverte && (participants.length > 0 || formateursPlanif.length > 0) && (
                  <div className="participants-resume">
                    {participants
                      .filter((p) => p.nom.trim() || p.prenom.trim() || p.email.trim())
                      .map((p, i) => (
                        <span className="participants-resume-puce" key={i} title={p.email}>
                          {[p.prenom, p.nom].filter((x) => x && x.trim()).join(' ') || p.email}
                        </span>
                      ))}
                    {formateursPlanif.map((f) => (
                      <span
                        className="participants-resume-puce participants-resume-formateur"
                        key={`f-${f.nom}`}
                        title={f.email}
                      >
                        {f.nom} · Formateur
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {saisieOuverte && (
              <>
              {/* Une grille plutôt qu'un tableau à défilement horizontal : les
                  champs prennent la largeur que leur contenu réclame, et la
                  ligne se replie en petite carte sur écran étroit. */}
              <div className="participants-liste">
                <div className="participants-entete" aria-hidden="true">
                  <span>Civilité</span>
                  <span>Prénom</span>
                  <span>Nom</span>
                  <span>Entreprise</span>
                  <span>Qualité</span>
                  <span>E-mail</span>
                  <span>Groupe</span>
                  <span />
                </div>

                {participants.length === 0 && (
                  <div className="participants-vide">Aucun participant enregistré.</div>
                )}

                {participants.map((p, i) => (
                  <div className="participants-ligne" key={i}>
                    <SelectRecherche
                      className="participants-civilite"
                      aria-label={`Civilité du participant ${i + 1}`}
                      value={p.civilite}
                      onChange={(e) => majParticipant(i, 'civilite', e.target.value)}
                    >
                      {CIVILITES.map((c) => (
                        <option key={c} value={c}>
                          {c || '—'}
                        </option>
                      ))}
                    </SelectRecherche>
                    <input
                      aria-label={`Prénom du participant ${i + 1}`}
                      placeholder="Prénom"
                      value={p.prenom}
                      onChange={(e) => majParticipant(i, 'prenom', e.target.value)}
                    />
                    <ChampMentions
                      as="input"
                      aria-label={`Nom du participant ${i + 1}`}
                      placeholder="Nom (ou @ pour chercher)"
                      value={p.nom}
                      onChange={(e) => majParticipant(i, 'nom', e.target.value)}
                      onChoisir={(personne) => choisirPersonneDansLigne(i, personne)}
                    />
                    <input
                      aria-label={`Entreprise du participant ${i + 1}`}
                      placeholder="Entreprise"
                      value={p.societe || ''}
                      onChange={(e) => majParticipant(i, 'societe', e.target.value)}
                    />
                    <input
                      aria-label={`Qualité du participant ${i + 1}`}
                      placeholder="Qualité"
                      value={p.qualite}
                      onChange={(e) => majParticipant(i, 'qualite', e.target.value)}
                    />
                    <input
                      type="email"
                      aria-label={`E-mail du participant ${i + 1}`}
                      placeholder="E-mail"
                      value={p.email}
                      onChange={(e) => majParticipant(i, 'email', e.target.value)}
                    />
                    <SelectRecherche
                      aria-label={`Groupe du participant ${i + 1}`}
                      value={p.groupeLibelle}
                      onChange={(e) => majParticipant(i, 'groupeLibelle', e.target.value)}
                    >
                      <option value="">Toutes les journées</option>
                      {groupesDesJournees.map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))}
                      {/* Un groupe hérité qui ne correspond à aucune journée : il
                          reste visible, sinon la valeur enregistrée disparaîtrait
                          du champ sans que rien ne le dise. */}
                      {p.groupeLibelle &&
                        !groupesDesJournees.some((g) => cleGroupe(g) === cleGroupe(p.groupeLibelle)) && (
                          <option value={p.groupeLibelle}>
                            {p.groupeLibelle} — aucune journée
                          </option>
                        )}
                    </SelectRecherche>
                    <button
                      type="button"
                      className="sessions-supprimer"
                      onClick={() => modifierParticipants((prev) => prev.filter((_, j) => j !== i))}
                      aria-label={`Retirer le participant ${i + 1}`}
                    >
                      ×
                    </button>
                  </div>
                ))}

                {/* Le formateur, lui aussi sur la liste : qualité « Formateur ». Lecture
                    seule ici, il se choisit sur la journée. */}
                {formateursPlanif.map((f) => (
                  <div
                    className="participants-ligne participants-ligne-formateur"
                    key={`formateur-${f.nom}`}
                    title="Le formateur se choisit dans la journée : il émarge sur chaque feuille et reçoit sa convocation à part."
                  >
                    <span className="participants-cellule-vide" />
                    <input
                      className="participants-formateur-nom"
                      aria-label="Nom du formateur"
                      value={f.nom}
                      readOnly
                      tabIndex={-1}
                    />
                    <input aria-label="Entreprise du formateur" value="—" readOnly tabIndex={-1} />
                    <input aria-label="Qualité du formateur" value="Formateur" readOnly tabIndex={-1} />
                    <input
                      aria-label="E-mail du formateur"
                      value={f.email}
                      placeholder="E-mail"
                      readOnly
                      tabIndex={-1}
                    />
                    <input aria-label="Groupe du formateur" value="Toutes les journées" readOnly tabIndex={-1} />
                    <span className="participants-cellule-vide" />
                  </div>
                ))}
              </div>

              <div className="sessions-actions">
                <button
                  type="button"
                  className="devis-btn devis-btn-sm"
                  onClick={() => modifierParticipants((prev) => [...prev, ligneParticipantVide()])}
                >
                  + Ajouter un participant
                </button>
                {participantsModifies && (
                  <span className="sessions-badge-attente">
                    Modifiés — enregistrés avec la planification
                  </span>
                )}
              </div>
              </>
              )}

              {/* ---------------- Sessions ---------------- */}
              <div className="devis-form-section-title sessions-section-titre">
                Journées planifiées
                {sessions.length - suppressions.size + ajouts.length > 0 && (
                  <span className="sessions-compteur">
                    {sessions.length - suppressions.size + ajouts.length}
                  </span>
                )}
              </div>

              {sessions.length === 0 && ajouts.length === 0 && (
                <div className="devis-drawer-empty">
                  Aucune session planifiée : reprenez une date du calendrier ou saisissez-la.
                </div>
              )}

              <div className="sessions-grille">
                {sessions.map((s) => {
                  const enEdition = edition?.id === s.id;
                  const aSupprimer = suppressions.has(s.id);
                  const modifiee = Boolean(modifications[s.id]);
                  // Tant qu'un changement attend, les gestes sur les documents
                  // produiraient ou enverraient l'ÉTAT D'AVANT : journée pas
                  // encore retouchée, ou liste d'élus pas encore enregistrée.
                  const brouillonBloque = participantsModifies || modifiee || aSupprimer;
                  const documentsVisibles = documentsOuverts.has(s.id);
                  // Convocation partie : la journée est figée — les élus ont
                  // reçu ces dates. Le serveur refuse de toute façon.
                  const verrouillee = (s.documents || []).some(
                    (d) => d.type === 'convocation' && d.date_envoi
                  );
                  // Qui cette journée convoque, d’après la règle du serveur :
                  // son groupe, plus ceux qui n’en portent aucun.
                  const convoques = s.groupe_libelle
                    ? participants.filter(
                        (p) =>
                          !cleGroupe(p.groupeLibelle) ||
                          cleGroupe(p.groupeLibelle) === cleGroupe(s.groupe_libelle)
                      )
                    : participants;
                  const requis = documentsRequis(s);
                  const prets = requis.filter(({ type }) =>
                    (s.documents || []).some((d) => d.type === type)
                  ).length;

                  return (
                    <div
                      className={
                        'sessions-carte' +
                        (enEdition ? ' sessions-carte-edition' : '') +
                        (aSupprimer ? ' sessions-carte-supprimee' : '') +
                        (modifiee && !aSupprimer ? ' sessions-carte-modifiee' : '')
                      }
                      key={s.id}
                    >
                      {(aSupprimer || modifiee) && (
                        <div className="sessions-badge-attente">
                          {aSupprimer
                            ? 'Sera supprimée à l’enregistrement'
                            : 'Modifiée — en attente d’enregistrement'}
                        </div>
                      )}
                      <div className="sessions-carte-tete">
                        <div className="sessions-carte-identite">
                          <div className="sessions-carte-titre">{s.intitule}</div>
                          <div className="sessions-carte-meta">
                            {versHeureInput(s.heure_debut)}–{versHeureInput(s.heure_fin)} ·{' '}
                            {s.lieu || 'lieu à définir'}
                          </div>
                        </div>
                        {s.groupe_libelle && (
                          <span className="sessions-groupe">{s.groupe_libelle}</span>
                        )}
                      </div>

                      {/* Les journées en pastilles de calendrier : un cycle
                          discontinu se lit d'un coup d'œil, sans phrase à
                          relire. En modification, le formulaire porte déjà
                          la liste éditable. */}
                      {!enEdition && (
                        <div className="sessions-puces">
                          {(s.jours || []).map((j) => {
                            const p = pastilleJour(j);
                            if (!p) return null;

                            return (
                              <span
                                className={`sessions-puce sessions-puce-${p.etat}`}
                                key={j}
                                title={formatDate(j)}
                              >
                                <span className="sessions-puce-semaine">{p.semaine}</span>
                                <span className="sessions-puce-jour">{p.jour}</span>
                                <span className="sessions-puce-mois">{p.mois}</span>
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {/* Une journée sans personne à convoquer produit une
                          feuille vide et refuse la convocation : mieux vaut le
                          dire ici que le découvrir dans le PDF. */}
                      {!enEdition && participants.length > 0 && convoques.length === 0 && (
                        <div className="sessions-alerte">
                          Aucun participant ne porte le groupe «&nbsp;{s.groupe_libelle}&nbsp;» :
                          la feuille sortirait vide.
                        </div>
                      )}

                      {!enEdition && (
                        <>
                          {s.formateurs && (
                            <div className="sessions-carte-ligne">
                              <span>Intervenant(e)s</span> {s.formateurs}
                            </div>
                          )}
                          {s.adresse && (
                            <div className="sessions-carte-ligne">
                              <span>Adresse</span> {s.adresse}
                            </div>
                          )}

                          {/* Une bascule plutôt qu'un bloc toujours ouvert : le
                              compte des documents prêts suffit tant qu'on ne
                              vient pas les générer. */}
                          <button
                            type="button"
                            className="sessions-documents-bascule"
                            onClick={() => basculerDocuments(s.id)}
                            aria-expanded={documentsVisibles}
                            aria-controls={`documents-session-${s.id}`}
                          >
                            <span className="sessions-documents-libelle">Documents</span>
                            <span
                              className={`sessions-documents-compte${
                                prets === requis.length ? ' est-complet' : ''
                              }`}
                            >
                              {prets}/{requis.length}
                            </span>
                            <svg
                              className="sessions-documents-fleche"
                              width="11"
                              height="11"
                              viewBox="0 0 24 24"
                              fill="none"
                              aria-hidden="true"
                            >
                              <path
                                d="M6 9l6 6 6-6"
                                stroke="currentColor"
                                strokeWidth="2.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>

                          {documentsVisibles && (
                            <div className="sessions-documents" id={`documents-session-${s.id}`}>
                              {documentsAffiches(s).map(({ type, label, court }) => {
                                const doc = (s.documents || []).find((d) => d.type === type);
                                const enCours = travail === `${s.id}:${type}`;
                                // La feuille de présence s'émarge en séance :
                                // elle ne s'adresse à personne.
                                const envoyable = type === 'convocation' && doc;
                                // La convocation annonce une journée : tant
                                // qu'un document de la session manque, le
                                // dossier n'est pas prêt à partir.
                                const dossierComplet = prets === requis.length;
                                const envoiEnCours = doc && travail === `envoi:${doc.id}`;

                                return (
                                  <div className="sessions-document" key={type}>
                                    <span
                                      className={`sessions-document-etat${doc ? ' est-pret' : ''}`}
                                      aria-hidden="true"
                                    />
                                    <div className="sessions-document-nom">
                                      {court}
                                      <span className="sessions-document-meta">
                                        {doc
                                          ? [
                                              `${doc.nb_participants} participant${
                                                doc.nb_participants > 1 ? 's' : ''
                                              }`,
                                              `généré le ${formatDateTime(doc.updated_at)}`,
                                              ...(type === 'convocation' ||
                                              type === 'convocation_formateur'
                                                ? [
                                                    doc.date_envoi
                                                      ? `envoyée le ${formatDateTime(doc.date_envoi)}`
                                                      : 'pas encore envoyée',
                                                  ]
                                                : []),
                                            ].join(' · ')
                                          : 'pas encore généré'}
                                      </span>
                                    </div>
                                    <div className="sessions-document-actions">
                                      {doc && (
                                        <button
                                          type="button"
                                          className="devis-btn devis-btn-sm"
                                          onClick={() => telecharger(doc)}
                                          disabled={travail === `dl:${doc.id}`}
                                        >
                                          {travail === `dl:${doc.id}` ? 'En cours' : 'Télécharger'}
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        className={`devis-btn devis-btn-sm${
                                          doc ? '' : ' devis-btn-primary'
                                        }`}
                                        onClick={() => produire(s, type)}
                                        disabled={enCours || brouillonBloque}
                                        title={
                                          brouillonBloque
                                            ? 'Enregistrez d’abord la planification'
                                            : `${doc ? 'Regénérer' : 'Générer'} : ${label}`
                                        }
                                      >
                                        {enCours ? 'En cours' : doc ? 'Regénérer' : 'Générer'}
                                      </button>
                                      {envoyable && (
                                        <button
                                          type="button"
                                          className={`devis-btn devis-btn-sm${
                                            doc.date_envoi ? '' : ' devis-btn-primary'
                                          }`}
                                          onClick={() => envoyerConvocation(doc)}
                                          disabled={
                                            Boolean(envoiEnCours) || !dossierComplet || brouillonBloque
                                          }
                                          title={
                                            brouillonBloque
                                              ? 'Enregistrez d’abord la planification'
                                              : !dossierComplet
                                              ? 'Générez tous les documents de la session avant l’envoi'
                                              : doc.date_envoi
                                                ? 'Renvoyer la convocation aux participants, copie au client'
                                                : 'Envoyer à chaque participant sa convocation, copie au client'
                                          }
                                        >
                                          {envoiEnCours
                                            ? 'Envoi'
                                            : doc.date_envoi
                                              ? 'Renvoyer'
                                              : 'Envoyer'}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          <div className="sessions-carte-pied">
                            <span className="sessions-carte-source">
                              {s.calendrier_id ? 'Calendrier' : 'Saisie manuelle'}
                            </span>
                            {/* La convocation partie n'empêche plus rien : un
                                simple rappel que les élus ont déjà reçu ces
                                dates — à reconvoquer si elles changent. */}
                            {verrouillee && (
                              <span
                                className="sessions-verrou"
                                title="Les élus ont déjà reçu ces dates : si vous les changez, pensez à renvoyer la convocation"
                              >
                                Convocation envoyée
                              </span>
                            )}
                            {aSupprimer ? (
                              <button
                                type="button"
                                className="devis-btn devis-btn-sm"
                                onClick={() => annulerSuppression(s.id)}
                              >
                                Garder cette journée
                              </button>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="devis-btn devis-btn-sm"
                                  onClick={() =>
                                    // Une retouche en attente se reprend là où on
                                    // l'avait laissée, pas depuis l'état enregistré.
                                    setEdition({
                                      id: s.id,
                                      form: {
                                        ...sessionVersFormulaire(s),
                                        ...(modifications[s.id] || {}),
                                      },
                                    })
                                  }
                                >
                                  Modifier
                                </button>
                                {modifiee && (
                                  <button
                                    type="button"
                                    className="devis-btn devis-btn-sm"
                                    onClick={() => annulerModification(s.id)}
                                  >
                                    Annuler la retouche
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="devis-btn devis-btn-sm devis-btn-danger"
                                  onClick={() => retirerSession(s)}
                                >
                                  Supprimer
                                </button>
                              </>
                            )}
                          </div>
                        </>
                      )}

                      {enEdition && (
                        <FormulaireSession
                          form={edition.form}
                          formateurs={formateurs}
                          onNouveauFormateur={() => setCreationFormateur('edition')}
                          onChange={(champ, valeur) =>
                            setEdition((prev) => ({
                              ...prev,
                              form: { ...prev.form, [champ]: valeur },
                            }))
                          }
                          actions={
                            <>
                              <button
                                type="button"
                                className="devis-btn devis-btn-sm"
                                onClick={() => setEdition(null)}
                              >
                                Annuler
                              </button>
                              <button
                                type="button"
                                className="devis-btn devis-btn-sm devis-btn-primary"
                                onClick={enregistrerEdition}
                              >
                                Garder ces modifications
                              </button>
                            </>
                          }
                        />
                      )}
                    </div>
                  );
                })}

                {/* Les journées ajoutées, pas encore créées : ni documents ni
                    envois tant que la planification n'est pas confirmée. */}
                {ajouts.map((a) => (
                  <div className="sessions-carte sessions-carte-brouillon" key={a.cle}>
                    <div className="sessions-badge-attente">
                      Nouvelle — créée à l’enregistrement
                    </div>
                    <div className="sessions-carte-tete">
                      <div className="sessions-carte-identite">
                        <div className="sessions-carte-titre">{a.apercu.intitule}</div>
                        <div className="sessions-carte-meta">{a.apercu.periode}</div>
                      </div>
                      {a.apercu.groupe && (
                        <span className="sessions-groupe">{a.apercu.groupe}</span>
                      )}
                    </div>
                    <div className="sessions-carte-ligne sessions-brouillon-note">
                      Convocation et feuille de présence seront produites à l’enregistrement.
                    </div>
                    <div className="sessions-carte-pied">
                      <span className="sessions-carte-source">{a.apercu.source}</span>
                      <button
                        type="button"
                        className="devis-btn devis-btn-sm devis-btn-danger"
                        onClick={() => setAjouts((prev) => prev.filter((x) => x.cle !== a.cle))}
                      >
                        Retirer
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* ---------------- Ajout ---------------- */}
              <div className="devis-form-section-title sessions-section-titre">
                Ajouter une session
              </div>

              <div className="sessions-ajout">
                <label className="devis-form-field">
                  <span>Depuis le calendrier</span>
                  <SelectRecherche
                    value={calendrierChoisi}
                    onChange={(e) => setCalendrierChoisi(e.target.value)}
                  >
                    <option value="">— Choisir une session programmée —</option>
                    {suggerees.length > 0 && (
                      <optgroup label="Formations du devis">
                        {suggerees.map(optionCalendrier)}
                      </optgroup>
                    )}
                    {autres.length > 0 && (
                      <optgroup label="Autres sessions du calendrier">
                        {autres.map(optionCalendrier)}
                      </optgroup>
                    )}
                  </SelectRecherche>
                </label>

                <label className="devis-form-field sessions-champ-court">
                  <span>Groupe (optionnel)</span>
                  <input
                    value={groupeAjout}
                    placeholder="Groupe 1"
                    onChange={(e) => setGroupeAjout(e.target.value)}
                  />
                </label>

                <label className="devis-form-field">
                  {/* Même choix que sur une journée : la fiche assignée reçoit
                      sa convocation, ses rappels et son lien d'émargement. Se
                      change ensuite depuis la journée (Modifier). */}
                  <span>Formateur / formatrice</span>
                  <SelectRecherche
                    value={formateurAjout}
                    onChange={(e) => setFormateurAjout(e.target.value)}
                  >
                    <option value="">— à préciser plus tard —</option>
                    {formateurs.map((f) => (
                      <option key={f.id} value={f.id}>
                        {[f.prenom, f.nom].filter(Boolean).join(' ')}
                        {f.fonction ? ` — ${f.fonction}` : ''}
                      </option>
                    ))}
                  </SelectRecherche>
                </label>
                <button
                  type="button"
                  className="devis-btn devis-btn-sm sessions-formateur-plus"
                  onClick={() => setCreationFormateur('ajout')}
                  title="Créer une fiche formateur sans quitter la planification"
                  aria-label="Nouveau formateur"
                >
                  +
                </button>

                <button
                  type="button"
                  className="devis-btn devis-btn-sm devis-btn-primary"
                  onClick={ajouterDepuisCalendrier}
                  disabled={!calendrierChoisi}
                >
                  Ajouter
                </button>
              </div>

              <p className="sessions-note">
                Dates recopiées à l’ajout : une convocation déjà envoyée n’est pas mise à jour.
              </p>

              {!saisieManuelle && (
                <button
                  type="button"
                  className="devis-btn devis-btn-sm"
                  onClick={() => {
                    setSaisieManuelle(true);
                    setFormManuel({
                      ...formulaireSessionVide(),
                      intitule: contrat.intitule_action || '',
                      lieu: contrat.lieu_formation || '',
                    });
                  }}
                >
                  Saisir des dates (INTRA)
                </button>
              )}

              {saisieManuelle && (
                <div className="sessions-carte">
                  <FormulaireSession
                    form={formManuel}
                    formateurs={formateurs}
                    onNouveauFormateur={() => setCreationFormateur('manuel')}
                    onChange={(champ, valeur) =>
                      setFormManuel((prev) => ({ ...prev, [champ]: valeur }))
                    }
                    actions={
                      <>
                        <button
                          type="button"
                          className="devis-btn devis-btn-sm"
                          onClick={() => setSaisieManuelle(false)}
                        >
                          Annuler
                        </button>
                        <button
                          type="button"
                          className="devis-btn devis-btn-sm devis-btn-primary"
                          onClick={ajouterManuellement}
                        >
                          Ajouter la session
                        </button>
                      </>
                    }
                  />
                </div>
              )}

              <BarreProgression valeur={progressionEnregistrement} libelle="Enregistrement de la planification" estimation />
              {/* Le seul bouton qui écrit. Collée au bas du panneau pour
                  rester sous la main quelle que soit la longueur de la liste. */}
              {creationFormateur && (
                <FormateurFormulaire
                  formateur={null}
                  onClose={() => setCreationFormateur(null)}
                  onEnregistre={formateurCree}
                />
              )}

              <div className="sessions-confirmation">
                <div className="sessions-confirmation-texte">
                  {enAttente ? (
                    <>
                      <strong>
                        {enAttente} modification{enAttente > 1 ? 's' : ''} en attente.
                      </strong>{' '}
                      Rien n’est créé ni envoyé avant l’enregistrement.
                    </>
                  ) : (
                    'Planification à jour : rien à enregistrer.'
                  )}
                </div>
                <div className="sessions-confirmation-actions">
                  <button
                    type="button"
                    className="devis-btn devis-btn-sm"
                    onClick={annulerTout}
                    disabled={!enAttente || confirmation}
                  >
                    Annuler les modifications
                  </button>
                  <button
                    type="button"
                    className="devis-btn devis-btn-primary"
                    onClick={() => enregistrerTout()}
                    disabled={!enAttente || confirmation}
                    title="Crée les journées, produit leurs documents et déclenche les envois dus"
                  >
                    {confirmation ? 'Enregistrement…' : 'Enregistrer la planification'}
                  </button>
                </div>
              </div>

              {/* La convocation est déjà partie et la liste des élus change :
                  qui doit recevoir un email ? Rien ne part avant la réponse. */}
              {choixEnvoi && (
                <div className="param-modal-overlay" onClick={() => setChoixEnvoi(false)}>
                  <div className="param-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="param-modal-header">
                      <h2 className="param-modal-title">Convocation déjà envoyée</h2>
                      <button
                        type="button"
                        className="param-modal-close"
                        onClick={() => setChoixEnvoi(false)}
                      >
                        ×
                      </button>
                    </div>
                    <div className="param-modal-body">
                      <p>
                        La liste des participants a changé alors que la convocation est déjà
                        partie. Que faut-il envoyer à l’enregistrement ?
                      </p>
                      <div className="sessions-choix-envoi">
                        <button
                          type="button"
                          className="devis-btn devis-btn-primary"
                          onClick={() => enregistrerTout('nouveaux')}
                        >
                          Convoquer seulement les nouveaux participants
                        </button>
                        <button
                          type="button"
                          className="devis-btn"
                          onClick={() => enregistrerTout('tous')}
                        >
                          Renvoyer la convocation à tous les participants
                        </button>
                        <button
                          type="button"
                          className="devis-btn"
                          onClick={() => enregistrerTout('aucun')}
                        >
                          Enregistrer sans envoyer d’email
                        </button>
                      </div>
                      <p className="sessions-note">
                        Dans tous les cas, les documents (convocations, feuilles de présence)
                        sont regénérés avec la liste à jour.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {apercuConvocation && (
        <ApercuEmail
          titre="Envoyer la convocation"
          sousTitre={apercuConvocation.session_intitule || contrat.numero}
          charger={() => apercuEnvoiDocument(apercuConvocation.id)}
          onEnvoyer={(edits) => envoyerConvocationApresApercu(apercuConvocation, edits)}
          onClose={() => setApercuConvocation(null)}
        />
      )}
    </div>
  );
}

/**
 * Formulaire commun à la création manuelle et à la modification : les mêmes
 * champs, y compris la liste des journées, qu'un cycle peut avoir discontinue.
 */
function FormulaireSession({ form, onChange, actions, formateurs = [], onNouveauFormateur = null }) {
  const [jour, setJour] = useState('');

  const ajouterJour = () => {
    if (!jour || form.jours.includes(jour)) return;
    onChange('jours', [...form.jours, jour].sort());
    setJour('');
  };

  return (
    <div className="sessions-form">
      <div className="sessions-form-ligne">
        <label className="devis-form-field">
          <span>Intitulé</span>
          <input value={form.intitule} onChange={(e) => onChange('intitule', e.target.value)} />
        </label>
        <label className="devis-form-field sessions-champ-court">
          <span>Groupe</span>
          <input
            value={form.groupeLibelle}
            placeholder="Groupe 1"
            onChange={(e) => onChange('groupeLibelle', e.target.value)}
          />
        </label>
      </div>

      <div className="sessions-form-ligne">
        <label className="devis-form-field sessions-champ-court">
          <span>Début</span>
          <input
            type="time"
            value={form.heureDebut}
            onChange={(e) => onChange('heureDebut', e.target.value)}
          />
        </label>
        <label className="devis-form-field sessions-champ-court">
          <span>Fin</span>
          <input
            type="time"
            value={form.heureFin}
            onChange={(e) => onChange('heureFin', e.target.value)}
          />
        </label>
        <label className="devis-form-field sessions-champ-court">
          <span>Modalité</span>
          <SelectRecherche value={form.modalite} onChange={(e) => onChange('modalite', e.target.value)}>
            {MODALITES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </SelectRecherche>
        </label>
        <label className="devis-form-field">
          <span>Lieu</span>
          <input value={form.lieu} onChange={(e) => onChange('lieu', e.target.value)} />
        </label>
      </div>

      <div className="sessions-form-ligne">
        <label className="devis-form-field">
          <span>Adresse exacte (imprimée sur la convocation)</span>
          <input value={form.adresse} onChange={(e) => onChange('adresse', e.target.value)} />
        </label>
        <label className="devis-form-field">
          {/* La fiche fait foi : nom et email sont recopiés sur la journée, et
              le formateur reçoit convocation, rappels et lien d'émargement. */}
          <span>Formateur / formatrice</span>
          <div className="sessions-formateur-choix">
            <SelectRecherche value={form.formateurId} onChange={(e) => onChange('formateurId', e.target.value)}>
              <option value="">— aucune fiche (saisie libre) —</option>
              {formateurs.map((f) => (
                <option key={f.id} value={f.id}>
                  {[f.prenom, f.nom].filter(Boolean).join(' ')}
                  {f.fonction ? ` — ${f.fonction}` : ''}
                </option>
              ))}
              {form.formateurId &&
                !formateurs.some((f) => String(f.id) === String(form.formateurId)) && (
                  <option value={form.formateurId}>Fiche retirée</option>
                )}
            </SelectRecherche>
            {onNouveauFormateur && (
              <button
                type="button"
                className="devis-btn devis-btn-sm sessions-formateur-plus"
                onClick={onNouveauFormateur}
                title="Créer une fiche formateur sans quitter la planification"
                aria-label="Nouveau formateur"
              >
                +
              </button>
            )}
          </div>
        </label>
      </div>

      {!form.formateurId && (
        <div className="sessions-form-ligne">
          <label className="devis-form-field">
            <span>Intervenant(e)s (sans fiche)</span>
            <input
              value={form.formateurs}
              placeholder="Prénom NOM, formateur/trice CSE"
              onChange={(e) => onChange('formateurs', e.target.value)}
            />
          </label>
          <label className="devis-form-field">
            {/* Sans elle, le formateur ne reçoit ni les rappels J-7 et J-3, ni
                son lien pour émarger le jour J. */}
            <span>Email du formateur (rappels et émargement)</span>
            <input
              type="email"
              value={form.formateurEmail}
              placeholder="prenom.nom@example.fr"
              onChange={(e) => onChange('formateurEmail', e.target.value)}
            />
          </label>
        </div>
      )}

      <div className="sessions-jours">
        <span className="sessions-jours-label">Journées</span>
        <div className="sessions-jours-liste">
          {form.jours.length === 0 && <span className="sessions-vide">Aucune journée</span>}
          {form.jours.map((j) => (
            <span className="sessions-jour" key={j}>
              {formatDate(j)}
              <button
                type="button"
                onClick={() => onChange('jours', form.jours.filter((x) => x !== j))}
                aria-label={`Retirer le ${formatDate(j)}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="sessions-jours-ajout">
          <input type="date" value={jour} onChange={(e) => setJour(e.target.value)} />
          <button type="button" className="devis-btn devis-btn-sm" onClick={ajouterJour}>
            + Journée
          </button>
        </div>
      </div>

      <div className="sessions-actions">{actions}</div>
    </div>
  );
}

export default SessionsPanel;
